"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { Box, Typography, IconButton, Paper, LinearProgress, useTheme } from "@mui/material"
import { Close as CloseIcon, CheckCircle } from "@mui/icons-material"

interface SuccessToastProps {
  open: boolean
  message: string
  onClose: () => void
}

export const SuccessToast = ({ open, message, onClose }: SuccessToastProps) => {
  const theme = useTheme()
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!open) return setProgress(0)

    const timer = setInterval(() => {
      setProgress((prev) => Math.min(prev + 2.5, 100))
    }, 100)

    return () => clearInterval(timer)
  }, [open])

  useEffect(() => {
    if (progress >= 100) setTimeout(onClose, 200)
  }, [progress, onClose])

  if (!open) return null

  return createPortal(
    <Box sx={{ position: "fixed", top: 20, right: 20, zIndex: 9999, minWidth: 300 }}>
      <Paper
        elevation={8}
        sx={{
          bgcolor: theme.palette.mode === "dark" ? "#1976d2" : "#fff",
          color: theme.palette.mode === "dark" ? "#fff" : "#333",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" p={1.5} pr={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color={theme.palette.mode === "dark" ? "inherit" : "primary"} />
            <Typography fontWeight={500}>{message}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            "& .MuiLinearProgress-bar": {
              bgcolor: theme.palette.mode === "dark" ? "#90caf9" : "#1976d2",
            },
          }}
        />
      </Paper>
    </Box>,
    document.body,
  )
}
